import { supabase, makeAuthenticatedRequest } from './supabaseClient';
import { useClerkToken } from './useClerkToken';
import { 
  UserProfile, 
  SubscriptionPlan, 
  UserSubscription, 
  FeatureFlag, 
  SubscriptionStatus, 
  FeatureAccess,
  UsageMetric,
  PLAN_NAMES,
  FEATURE_NAMES,
  USAGE_METRICS
} from '../types/subscription';

export class SubscriptionService {
  // User Profile Management
  static async createUserProfile(clerkUserId: string, email: string, firstName?: string, lastName?: string): Promise<UserProfile> {
    try {
      // First, check if a profile with this clerk_user_id already exists (even if deleted)
      const { data: existingProfile, error: checkError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('clerk_user_id', clerkUserId)
        .maybeSingle();

      if (checkError) {
        console.error('Error checking existing profile:', checkError);
      }

      // If profile exists, try to update it instead of creating new one
      if (existingProfile) {
        console.log('Profile exists, updating instead of creating new one');
        const { data, error } = await supabase
          .from('user_profiles')
          .update({
            email,
            first_name: firstName,
            last_name: lastName,
            updated_at: new Date().toISOString()
          })
          .eq('clerk_user_id', clerkUserId)
          .select()
          .single();

        if (error) {
          console.error('Error updating existing profile:', error);
          throw error;
        }
        return data;
      }

      // Create new profile
      console.log('Creating new user profile for:', clerkUserId);
      
      // Try with RLS first
      let { data, error } = await supabase
        .from('user_profiles')
        .insert({
          clerk_user_id: clerkUserId,
          email,
          first_name: firstName,
          last_name: lastName,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select()
        .single();

      // If RLS fails, try with service role (development only)
      if (error && (error.code === '406' || error.code === '409' || error.message.includes('406') || error.message.includes('409'))) {
        console.log('RLS policy failed, trying with service role...');
        
        // Create a service role client for development
        const serviceRoleKey = process.env.REACT_APP_SUPABASE_SERVICE_ROLE_KEY;
        if (serviceRoleKey) {
          const { createClient } = await import('@supabase/supabase-js');
          const supabaseAdmin = createClient(
            process.env.REACT_APP_SUPABASE_URL!,
            serviceRoleKey,
            {
              auth: {
                autoRefreshToken: false,
                persistSession: false
              }
            }
          );
          
          const { data: adminData, error: adminError } = await supabaseAdmin
            .from('user_profiles')
            .insert({
              clerk_user_id: clerkUserId,
              email,
              first_name: firstName,
              last_name: lastName,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            })
            .select()
            .single();

          if (adminError) {
            console.error('Service role creation failed:', adminError);
            throw adminError;
          }
          
          console.log('Profile created with service role');
          return adminData;
        } else {
          console.error('Service role key not available');
          throw error;
        }
      }

      if (error) {
        console.error('Error creating user profile:', error);
        
        // If it's a constraint violation, try to get the existing profile
        if (error.code === '23505') { // Unique constraint violation
          console.log('Profile already exists, fetching existing profile');
          return await this.getUserProfile(clerkUserId) || this.createUserProfile(clerkUserId, email, firstName, lastName);
        }
        
        throw error;
      }
      return data;
    } catch (error) {
      console.error('Error creating user profile:', error);
      throw error;
    }
  }

  static async getUserProfile(clerkUserId: string): Promise<UserProfile | null> {
    try {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('clerk_user_id', clerkUserId)
      .single();

      if (error && error.code !== 'PGRST116') throw error;
    return data;
    } catch (error) {
      console.error('Error getting user profile:', error);
      return null;
    }
  }

  static async updateUserProfile(clerkUserId: string, updates: Partial<UserProfile>): Promise<UserProfile> {
    const { data, error } = await supabase
      .from('user_profiles')
      .update(updates)
      .eq('clerk_user_id', clerkUserId)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  // Subscription Management
  static async getCurrentSubscription(userId: string): Promise<UserSubscription | null> {
    const { data, error } = await supabase
      .from('user_subscriptions')
      .select('*')
      .eq('user_id', userId)
      .eq('status', 'active')
      .single();

    if (error && error.code !== 'PGRST116') throw error;
    return data;
  }

  static async createSubscription(userId: string, planId: string, stripeSubscriptionId?: string): Promise<UserSubscription> {
    const { data, error } = await supabase
      .from('user_subscriptions')
      .insert({
        user_id: userId,
        plan_id: planId,
        stripe_subscription_id: stripeSubscriptionId,
        status: 'active'
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  static async updateSubscription(subscriptionId: string, updates: Partial<UserSubscription>): Promise<UserSubscription> {
    const { data, error } = await supabase
      .from('user_subscriptions')
      .update(updates)
      .eq('id', subscriptionId)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  // Plan Management
  static async getSubscriptionPlans(): Promise<SubscriptionPlan[]> {
    const { data, error } = await supabase
      .from('subscription_plans')
      .select('*')
      .eq('is_active', true)
      .order('price_monthly', { ascending: true });

    if (error) throw error;
    return data || [];
  }

  static async getSubscriptionPlan(planId: string): Promise<SubscriptionPlan | null> {
    const { data, error } = await supabase
      .from('subscription_plans')
      .select('*')
      .eq('id', planId)
      .single();

    if (error && error.code !== 'PGRST116') throw error;
    return data;
  }

  // Feature Access Management
  static async getFeatureAccess(userId: string): Promise<FeatureAccess[]> {
    // TEMPORARILY DISABLED: All features are enabled with unlimited limits
    return [
      { feature: 'reviews', isEnabled: true, limits: { reviews_created: -1 } },
      { feature: 'analytics', isEnabled: true, limits: { analytics_views: -1 } },
      { feature: 'slack', isEnabled: true, limits: { slack_integrations: -1 } },
      { feature: 'branding', isEnabled: true, limits: { branding_customizations: -1 } },
      { feature: 'api', isEnabled: true, limits: { api_calls: -1 } },
      { feature: 'storage', isEnabled: true, limits: { storage_used_gb: -1 } }
    ];
  }

  static async hasFeatureAccess(userId: string, featureName: string): Promise<boolean> {
    // TEMPORARILY DISABLED: All features are enabled
    return true;
  }

  static async checkFeatureLimit(userId: string, featureName: string, metricName: string): Promise<{ allowed: boolean; current: number; limit: number }> {
    // TEMPORARILY DISABLED: All subscription limits are disabled
    return { allowed: true, current: 0, limit: -1 }; // Unlimited access
  }

  // Usage Tracking
  static async getCurrentUsage(userId: string, metricName: string): Promise<number> {
    const now = new Date();
    const periodStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const periodEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    const { data, error } = await supabase
      .from('usage_metrics')
      .select('metric_value')
      .eq('user_id', userId)
      .eq('metric_name', metricName)
      .eq('period_start', periodStart.toISOString().split('T')[0])
      .single();

    if (error && error.code !== 'PGRST116') throw error;
    return data?.metric_value || 0;
  }

  static async incrementUsage(userId: string, metricName: string, increment: number = 1): Promise<void> {
    const now = new Date();
    const periodStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const periodEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    // Try to update existing record
    const { error: updateError } = await supabase
      .from('usage_metrics')
      .update({ 
        metric_value: supabase.rpc('increment', { x: increment }),
        updated_at: new Date().toISOString()
      })
      .eq('user_id', userId)
      .eq('metric_name', metricName)
      .eq('period_start', periodStart.toISOString().split('T')[0]);

    // If no existing record, create one
    if (updateError && updateError.code === 'PGRST116') {
      const { error: insertError } = await supabase
        .from('usage_metrics')
        .insert({
          user_id: userId,
          metric_name: metricName,
          metric_value: increment,
          period_start: periodStart.toISOString().split('T')[0],
          period_end: periodEnd.toISOString().split('T')[0]
        });

      if (insertError) throw insertError;
    } else if (updateError) {
      throw updateError;
    }
  }

  // Comprehensive Subscription Status
  static async getSubscriptionStatus(clerkUserId: string): Promise<SubscriptionStatus | null> {
    const userProfile = await this.getUserProfile(clerkUserId);
    if (!userProfile) return null;

    const subscription = await this.getCurrentSubscription(userProfile.id);
    const features = await this.getFeatureAccess(userProfile.id);
    
    // Get current usage for all metrics
    const usage: Record<string, number> = {};
    for (const metric of Object.values(USAGE_METRICS)) {
      usage[metric] = await this.getCurrentUsage(userProfile.id, metric);
    }

    // Get plan details
    const plan = subscription ? await this.getSubscriptionPlan(subscription.plan_id) : await this.getSubscriptionPlanByName(PLAN_NAMES.FREE);
    
    if (!plan) return null;

    return {
      currentPlan: plan,
      subscription: subscription || {
        id: '',
        user_id: userProfile.id,
        plan_id: plan.id,
        status: 'active',
        cancel_at_period_end: false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      },
      features,
      usage,
      limits: plan.limits
    };
  }

  // Helper methods
  static async getSubscriptionPlanByName(planName: string): Promise<SubscriptionPlan | null> {
    const { data, error } = await supabase
      .from('subscription_plans')
      .select('*')
      .eq('name', planName)
      .single();

    if (error && error.code !== 'PGRST116') throw error;
    return data;
  }

  // Initialize user profile if it doesn't exist
  static async ensureUserProfile(clerkUserId: string, email: string, firstName?: string, lastName?: string): Promise<UserProfile> {
    try {
      let profile = await this.getUserProfile(clerkUserId);
      
      if (!profile) {
        console.log('No existing profile found, creating new one');
        profile = await this.createUserProfile(clerkUserId, email, firstName, lastName);
      } else {
        console.log('Existing profile found:', profile.id);
      }
      
      return profile;
    } catch (error) {
      console.error('Error ensuring user profile:', error);
      
      // If creation fails, try one more time after a short delay
      if (error instanceof Error && (error.message.includes('406') || error.message.includes('409'))) {
        console.log('Retrying profile creation after delay...');
        await new Promise(resolve => setTimeout(resolve, 1000));
        return await this.createUserProfile(clerkUserId, email, firstName, lastName);
      }
      
      throw error;
    }
  }
} 